<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useTheme } from '@/composables/useTheme'
import AdminSparkline from '@/components/AdminSparkline.vue'

type MetricEvent =
  | 'plan_uploaded'
  | 'plan_opened'
  | 'comment_created'
  | 'feedback_copied'
  | 'revision_seeded'
  | 'portable_downloaded'

type HitBucket = 'landing' | 'plan' | 'other'
type HitKind = 'browser' | 'automated'

interface RankedRow {
  value: string
  browser: number
  automated: number
}

interface Summary {
  generatedAt: string
  from: string
  days: number
  totals: Record<MetricEvent, number>
  daily: Array<{ day: string; event: MetricEvent; count: number }>
  hits: {
    daily: Array<{ day: string; bucket: HitBucket; kind: HitKind; count: number }>
    referrers: Record<HitBucket, RankedRow[]>
    paths: RankedRow[]
  }
  storage: { plans: number; snapshots: number; bytes: number }
  rateLimits: {
    limits: Array<{ name: string; limit: number; windowSeconds: number; failClosed: boolean }>
    denied: Array<{ day: string; name: string; count: number }>
    live: Array<{ name: string; clients: number; limited: number }>
  }
}

interface Card {
  label: string
  value: string
  series: number[]
  sub?: string
  format?: (value: number) => string
}

useTheme()

const columns: Array<{ event: MetricEvent; label: string }> = [
  { event: 'plan_uploaded', label: 'Uploads' },
  { event: 'plan_opened', label: 'Opens' },
  { event: 'comment_created', label: 'Comments' },
  { event: 'feedback_copied', label: 'Feedback' },
  { event: 'revision_seeded', label: 'Revisions' },
  { event: 'portable_downloaded', label: 'Downloads' },
]

const buckets: Array<{ bucket: HitBucket; label: string; hint: string }> = [
  { bucket: 'landing', label: 'Home page', hint: 'Browser visits to redline.ink/ (reported by the page itself)' },
  { bucket: 'plan', label: 'Plan links', hint: 'Requests for shared /plan/… pages' },
  { bucket: 'other', label: 'Everything else', hint: 'Scans, probes, reference-file fetches, typos' },
]

/** Stored stand-ins, shown as muted labels instead of URLs. */
const PSEUDO_VALUES: Record<string, string> = {
  '': 'Direct / no referrer',
  '(overflow)': 'Past the daily cap (500 distinct)',
}

const LIMITER_LABELS: Record<string, string> = {
  upload: 'Plan uploads',
  review: 'Review snapshots',
  event: 'Events & visits',
  'admin-login': 'Admin login',
}

const configured = ref(true)
const authenticated = ref(false)
const loading = ref(true)
const refreshing = ref(false)
const loggingIn = ref(false)
const error = ref('')
const passphrase = ref('')
const code = ref('')
const days = ref(30)
const summary = ref<Summary | null>(null)
const refBucket = ref<HitBucket>('landing')

/** Every UTC day in the range, oldest first, so series have no gaps. */
const dayList = computed(() => {
  if (!summary.value) return []
  const start = Date.parse(`${summary.value.from}T00:00:00Z`)
  return Array.from({ length: summary.value.days }, (_, i) =>
    new Date(start + i * 86_400_000).toISOString().slice(0, 10),
  )
})

function seriesFrom<T extends { day: string; count: number }>(rows: T[], keep: (row: T) => boolean): number[] {
  const byDay = new Map<string, number>()
  for (const row of rows) if (keep(row)) byDay.set(row.day, (byDay.get(row.day) ?? 0) + row.count)
  return dayList.value.map((day) => byDay.get(day) ?? 0)
}
const eventSeries = (event: MetricEvent) => seriesFrom(summary.value?.daily ?? [], (r) => r.event === event)
const hitSeries = (bucket: HitBucket | null, kind: HitKind | null) =>
  seriesFrom(summary.value?.hits.daily ?? [], (r) => (!bucket || r.bucket === bucket) && (!kind || r.kind === kind))
const sum = (values: number[]) => values.reduce((a, b) => a + b, 0)
const n = (value: number) => value.toLocaleString()
const pct = (value: number) => `${Math.round(value * 100)}%`

const trafficCards = computed<Card[]>(() => {
  if (!summary.value) return []
  const home = hitSeries('landing', 'browser')
  const plan = hitSeries('plan', 'browser')
  const planBots = sum(hitSeries('plan', 'automated'))
  const other = hitSeries('other', null)
  const otherBrowser = sum(hitSeries('other', 'browser'))
  const automated = hitSeries(null, 'automated')
  return [
    { label: 'Home page visits', value: n(sum(home)), series: home },
    { label: 'Plan link visits', value: n(sum(plan)), series: plan, sub: `+ ${n(planBots)} automated` },
    { label: 'Everything else', value: n(sum(other)), series: other, sub: `${n(otherBrowser)} from browsers` },
    { label: 'Automated requests', value: n(sum(automated)), series: automated, sub: 'bots, scripts, unfurlers' },
  ]
})

const productCards = computed<Card[]>(() => {
  if (!summary.value) return []
  const totals = summary.value.totals
  const opens = eventSeries('plan_opened')
  const feedback = eventSeries('feedback_copied')
  const ratio = opens.map((o, i) => (o ? feedback[i] / o : 0))
  const card = (label: string, event: MetricEvent): Card => ({
    label,
    value: n(totals[event]),
    series: eventSeries(event),
  })
  return [
    card('Plans uploaded', 'plan_uploaded'),
    card('Plans opened', 'plan_opened'),
    card('Feedback copied', 'feedback_copied'),
    {
      label: 'Open → feedback',
      value: totals.plan_opened ? pct(totals.feedback_copied / totals.plan_opened) : '—',
      series: ratio,
      format: pct,
    },
    card('Comments created', 'comment_created'),
    card('Revisions seeded', 'revision_seeded'),
  ]
})

const bucketTotals = computed(() => {
  const totals: Record<HitBucket, number> = { landing: 0, plan: 0, other: 0 }
  for (const row of summary.value?.hits.daily ?? []) totals[row.bucket] += row.count
  return totals
})

function rankedRows(rows: RankedRow[], total: number) {
  return rows.map((row) => ({
    ...row,
    label: PSEUDO_VALUES[row.value] ?? row.value,
    pseudo: row.value in PSEUDO_VALUES,
    share: total ? pct((row.browser + row.automated) / total) : '—',
  }))
}
const referrerRows = computed(() =>
  rankedRows(summary.value?.hits.referrers[refBucket.value] ?? [], bucketTotals.value[refBucket.value]),
)
const pathRows = computed(() => rankedRows(summary.value?.hits.paths ?? [], bucketTotals.value.other))
const showAutomated = computed(() => refBucket.value !== 'landing')

const dailyRows = computed(() => {
  const events = new Map<string, Partial<Record<MetricEvent, number>>>()
  for (const row of summary.value?.daily ?? []) {
    const values = events.get(row.day) ?? {}
    values[row.event] = row.count
    events.set(row.day, values)
  }
  const hits = new Map<string, { landing: number; plan: number; other: number; automated: number }>()
  for (const row of summary.value?.hits.daily ?? []) {
    const values = hits.get(row.day) ?? { landing: 0, plan: 0, other: 0, automated: 0 }
    values[row.bucket] += row.count
    if (row.kind === 'automated') values.automated += row.count
    hits.set(row.day, values)
  }
  return [...dayList.value]
    .reverse()
    .filter((day) => events.has(day) || hits.has(day))
    .map((day) => ({
      day,
      events: events.get(day) ?? {},
      hits: hits.get(day) ?? { landing: 0, plan: 0, other: 0, automated: 0 },
    }))
})

const limiterRows = computed(() => {
  const rl = summary.value?.rateLimits
  if (!rl) return []
  return rl.limits.map((l) => {
    const series = seriesFrom(rl.denied, (r) => r.name === l.name)
    const live = rl.live.find((x) => x.name === l.name) ?? { clients: 0, limited: 0 }
    return {
      ...l,
      label: LIMITER_LABELS[l.name] ?? l.name,
      rule: `${n(l.limit)} / ${l.windowSeconds / 60} min`,
      series,
      denied: sum(series),
      clients: live.clients,
      limitedNow: live.limited,
    }
  })
})

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`
}

async function readJson<T>(response: Response): Promise<T> {
  const body = (await response.json()) as T & { message?: string }
  if (!response.ok) throw new Error(body.message || 'Request failed.')
  return body
}

async function loadSummary(): Promise<void> {
  if (summary.value) refreshing.value = true
  else loading.value = true
  error.value = ''
  try {
    const response = await fetch(`/api/admin/summary?days=${days.value}`, {
      credentials: 'same-origin',
    })
    if (response.status === 401) {
      authenticated.value = false
      summary.value = null
      return
    }
    summary.value = await readJson<Summary>(response)
    loadedAt = Date.now()
    authenticated.value = true
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Could not load analytics.'
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

async function checkSession(): Promise<void> {
  try {
    const response = await fetch('/api/admin/session', { credentials: 'same-origin' })
    const session = await readJson<{ configured: boolean; authenticated: boolean }>(response)
    configured.value = session.configured
    authenticated.value = session.authenticated
    if (session.authenticated) await loadSummary()
  } catch {
    error.value = 'Could not reach the admin API.'
  } finally {
    loading.value = false
  }
}

async function login(): Promise<void> {
  if (loggingIn.value) return
  loggingIn.value = true
  error.value = ''
  try {
    const response = await fetch('/api/admin/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passphrase: passphrase.value, code: code.value }),
    })
    await readJson<{ ok: boolean }>(response)
    passphrase.value = ''
    code.value = ''
    authenticated.value = true
    await loadSummary()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Login failed.'
  } finally {
    loggingIn.value = false
  }
}

async function logout(): Promise<void> {
  await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' })
  authenticated.value = false
  summary.value = null
}

// ---- auto-refresh ----
// Poll while the tab is visible; catch up at once when it becomes visible
// again after being hidden past an interval.
const REFRESH_MS = 60_000
let refreshTimer: ReturnType<typeof setInterval> | null = null
// Local clock, not the server's generatedAt, so a skewed laptop clock can't
// turn polling into a 5-second loop.
let loadedAt = 0

function refreshIfStale(): void {
  if (!authenticated.value || refreshing.value || loading.value || document.hidden) return
  if (Date.now() - loadedAt >= REFRESH_MS - 1000) void loadSummary()
}

// ---- ← / → jump between sections ----
const page = ref<HTMLElement | null>(null)

function scrollBehavior(): ScrollBehavior {
  return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

/** Scroll offsets that put each [data-nav] section just under the sticky bar. */
function sectionOffsets(): number[] {
  const el = page.value
  if (!el) return []
  const bar = el.querySelector('.admin-bar')?.getBoundingClientRect().height ?? 0
  const top = el.getBoundingClientRect().top
  return [...el.querySelectorAll<HTMLElement>('[data-nav]')].map(
    (s) => s.getBoundingClientRect().top - top + el.scrollTop - bar - 12,
  )
}

// Where an in-flight smooth scroll is headed, so rapid presses step from the
// previous destination instead of re-picking the section mid-animation.
let navTarget: number | null = null
let navTimer: ReturnType<typeof setTimeout> | null = null

function jumpSection(direction: 1 | -1): void {
  const el = page.value
  if (!el) return
  const max = el.scrollHeight - el.clientHeight
  const now = navTarget ?? el.scrollTop
  const offsets = sectionOffsets()
  // Next = the first section below the current position (while the page can
  // still scroll down); previous = the last one above it, or the very top.
  const target =
    direction === 1
      ? offsets.find((o) => o > now + 4 && now < max - 1)
      : [...offsets].reverse().find((o) => o < now - 4) ?? (now > 0 ? 0 : undefined)
  if (target === undefined) return
  navTarget = Math.min(max, Math.max(0, target))
  el.scrollTo({ top: navTarget, behavior: scrollBehavior() })
  if (navTimer) clearTimeout(navTimer)
  navTimer = setTimeout(() => (navTarget = null), 700)
}

function onKeydown(e: KeyboardEvent): void {
  if (!authenticated.value || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
  if (e.target instanceof Element && e.target.closest('input, select, textarea, [contenteditable="true"]')) return
  e.preventDefault()
  jumpSection(e.key === 'ArrowRight' ? 1 : -1)
}

onMounted(() => {
  void checkSession()
  refreshTimer = setInterval(refreshIfStale, 5_000)
  document.addEventListener('visibilitychange', refreshIfStale)
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  if (refreshTimer) clearInterval(refreshTimer)
  document.removeEventListener('visibilitychange', refreshIfStale)
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <main ref="page" class="admin-page">
    <section v-if="!authenticated" class="admin-login" aria-labelledby="admin-title">
      <a class="admin-brand" href="/"><span aria-hidden="true">⌁</span> Redline</a>
      <h1 id="admin-title">Admin analytics</h1>

      <p v-if="!configured" class="admin-alert">Admin credentials have not been configured.</p>
      <form v-else class="admin-form" @submit.prevent="login">
        <label>
          <span>Passphrase</span>
          <input v-model="passphrase" type="password" autocomplete="current-password" required autofocus />
        </label>
        <label>
          <span>Authenticator code</span>
          <input
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9 ]{6,7}"
            maxlength="7"
            placeholder="000 000"
            required
          />
        </label>
        <p v-if="error" class="admin-alert" role="alert">{{ error }}</p>
        <button class="btn btn-primary btn-lg" :disabled="loading || loggingIn">
          {{ loggingIn ? 'Signing in…' : 'Sign in' }}
        </button>
      </form>
    </section>

    <section v-else class="admin-dashboard">
      <header class="admin-bar">
        <a class="admin-brand" href="/"><span aria-hidden="true">⌁</span> Redline</a>
        <span class="admin-bar-title">Analytics</span>
        <div class="admin-actions">
          <span class="admin-hint" aria-hidden="true"><kbd>←</kbd><kbd>→</kbd> sections</span>
          <span v-if="summary" class="admin-updated" title="Refreshes automatically every minute">
            {{ refreshing ? 'Refreshing…' : `Updated ${new Date(summary.generatedAt).toLocaleTimeString()}` }}
          </span>
          <select v-model="days" aria-label="Date range" @change="loadSummary">
            <option :value="7">Last 7 days</option>
            <option :value="30">Last 30 days</option>
            <option :value="90">Last 90 days</option>
          </select>
          <button class="btn btn-default btn-sm" :disabled="refreshing" @click="loadSummary">Refresh</button>
          <button class="btn btn-default btn-sm" @click="logout">Sign out</button>
        </div>
      </header>

      <p class="admin-lead">
        Aggregate UTC-day counters since {{ summary?.from ?? '…' }}. No visitors, documents, or plan slugs —
        referrers are stored as sent, paths only for the “everything else” bucket.
      </p>

      <p v-if="error" class="admin-alert" role="alert">{{ error }}</p>
      <div v-if="loading && !summary" class="admin-empty">Loading analytics…</div>

      <div v-else-if="summary" class="admin-body" :class="{ 'is-refreshing': refreshing }">
        <h2 class="admin-section" data-nav>Traffic</h2>
        <div class="admin-cards admin-cards-4">
          <article v-for="card in trafficCards" :key="card.label" class="admin-card">
            <span class="admin-card-label">{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
            <span v-if="card.sub" class="admin-card-sub">{{ card.sub }}</span>
            <AdminSparkline :values="card.series" :days="dayList" :format="card.format" />
          </article>
        </div>

        <h2 class="admin-section" data-nav>Product</h2>
        <div class="admin-cards">
          <article v-for="card in productCards" :key="card.label" class="admin-card">
            <span class="admin-card-label">{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
            <AdminSparkline :values="card.series" :days="dayList" :format="card.format" />
          </article>
        </div>

        <h2 class="admin-section" data-nav>Rate limiting</h2>
        <section class="admin-panel admin-panel-flush">
          <div class="admin-table-wrap">
            <table class="admin-limits">
              <thead>
                <tr>
                  <th>Limiter</th>
                  <th>Per client</th>
                  <th>If D1 is down</th>
                  <th>Active now</th>
                  <th>Over limit now</th>
                  <th>Denied</th>
                  <th class="admin-limits-trend">Denied per day</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in limiterRows" :key="row.name">
                  <td>{{ row.label }}</td>
                  <td>{{ row.rule }}</td>
                  <td :class="row.failClosed ? 'admin-closed' : 'admin-muted'">{{ row.failClosed ? 'Deny' : 'Allow' }}</td>
                  <td>{{ n(row.clients) }}</td>
                  <td :class="{ 'admin-warn': row.limitedNow > 0 }">{{ n(row.limitedNow) }}</td>
                  <td :class="{ 'admin-warn': row.denied > 0 }">{{ n(row.denied) }}</td>
                  <td class="admin-limits-trend"><AdminSparkline :values="row.series" :days="dayList" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="admin-footnote">
            Clients are IPv4 addresses or IPv6 /64s, counted over a sliding window and stored only as keyed hashes.
            “Active now” is clients with requests in the current window.
          </p>
        </section>

        <section class="admin-panel" data-nav>
          <div class="admin-panel-head">
            <div>
              <h3>Referrers</h3>
              <p>{{ buckets.find((b) => b.bucket === refBucket)?.hint }}</p>
            </div>
            <div class="admin-tabs" role="tablist" aria-label="Page bucket">
              <button
                v-for="b in buckets"
                :key="b.bucket"
                role="tab"
                class="admin-tab"
                :class="{ 'is-active': refBucket === b.bucket }"
                :aria-selected="refBucket === b.bucket"
                @click="refBucket = b.bucket"
              >
                {{ b.label }} <span class="admin-tab-count">{{ n(bucketTotals[b.bucket]) }}</span>
              </button>
            </div>
          </div>
          <div class="admin-table-wrap">
            <table class="admin-ranked">
              <thead>
                <tr>
                  <th>Referrer</th>
                  <th>{{ showAutomated ? 'Browser' : 'Visits' }}</th>
                  <th v-if="showAutomated">Automated</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="referrerRows.length === 0">
                  <td :colspan="showAutomated ? 4 : 3" class="admin-empty">Nothing recorded in this range.</td>
                </tr>
                <tr v-for="row in referrerRows" :key="row.value">
                  <td :class="{ 'admin-pseudo': row.pseudo }" :title="row.pseudo ? undefined : row.value">{{ row.label }}</td>
                  <td>{{ n(row.browser) }}</td>
                  <td v-if="showAutomated">{{ n(row.automated) }}</td>
                  <td>{{ row.share }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="admin-panel" data-nav>
          <div class="admin-panel-head">
            <div>
              <h3>Top paths — everything else</h3>
              <p>What scanners and stray requests are asking for (path only, no query).</p>
            </div>
          </div>
          <div class="admin-table-wrap">
            <table class="admin-ranked">
              <thead><tr><th>Path</th><th>Browser</th><th>Automated</th><th>Share</th></tr></thead>
              <tbody>
                <tr v-if="pathRows.length === 0"><td colspan="4" class="admin-empty">Nothing recorded in this range.</td></tr>
                <tr v-for="row in pathRows" :key="row.value">
                  <td :class="{ 'admin-pseudo': row.pseudo }" :title="row.pseudo ? undefined : row.value"><code>{{ row.label }}</code></td>
                  <td>{{ n(row.browser) }}</td>
                  <td>{{ n(row.automated) }}</td>
                  <td>{{ row.share }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="admin-panel" data-nav>
          <div class="admin-panel-head">
            <div>
              <h3>Daily</h3>
              <p>Newest first; days with no activity are omitted.</p>
            </div>
          </div>
          <div class="admin-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Home</th>
                  <th>Plan links</th>
                  <th>Other</th>
                  <th>Automated</th>
                  <th v-for="column in columns" :key="column.event">{{ column.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="dailyRows.length === 0"><td :colspan="columns.length + 5" class="admin-empty">No activity yet.</td></tr>
                <tr v-for="row in dailyRows" :key="row.day">
                  <td>{{ row.day }}</td>
                  <td>{{ n(row.hits.landing) }}</td>
                  <td>{{ n(row.hits.plan) }}</td>
                  <td>{{ n(row.hits.other) }}</td>
                  <td>{{ n(row.hits.automated) }}</td>
                  <td v-for="column in columns" :key="column.event">{{ n(row.events[column.event] ?? 0) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <h2 class="admin-section" data-nav>Storage</h2>
        <section class="admin-panel admin-storage">
          <div><span>Encrypted plans</span><strong>{{ n(summary.storage.plans) }}</strong></div>
          <div><span>Review snapshots</span><strong>{{ n(summary.storage.snapshots) }}</strong></div>
          <div><span>Total encrypted storage</span><strong>{{ formatBytes(summary.storage.bytes) }}</strong></div>
        </section>
      </div>
    </section>
  </main>
</template>

<style scoped>
/* The app shell pins body (overflow: hidden) for the reader, so this page is
   its own scroll container: fixed to the viewport height, scrolling inside. */
.admin-page { height: 100%; overflow-y: auto; background: var(--bg); color: var(--text); }
.admin-login { width: min(420px, calc(100% - 32px)); margin: 10vh auto 48px; padding: 32px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-md); }
.admin-brand { display: inline-flex; gap: 8px; align-items: center; color: var(--text); text-decoration: none; font-weight: 650; }
.admin-brand span { color: var(--mark); font-size: 24px; line-height: 1; }
h1 { margin: 24px 0 6px; font-size: 28px; line-height: 1.15; }
h3 { margin: 0; font-size: 16px; }
p { color: var(--text-muted); }
.admin-form { display: grid; gap: 18px; margin-top: 28px; }
.admin-form label { display: grid; gap: 7px; font-size: var(--text-md); font-weight: 600; }
input, select { width: 100%; min-height: 42px; padding: 0 12px; color: var(--text); background: var(--bg); border: 1px solid var(--border-strong); border-radius: var(--radius-sm); font: inherit; }
input:focus, select:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.admin-alert { padding: 10px 12px; color: var(--danger); background: color-mix(in srgb, var(--danger) 9%, transparent); border-radius: var(--radius-sm); }

.admin-dashboard { width: min(1180px, 100%); margin: 0 auto; padding: 0 24px 64px; }
.admin-bar { position: sticky; top: 0; z-index: 5; display: flex; align-items: center; gap: 12px; padding: 14px 0; background: color-mix(in srgb, var(--bg) 92%, transparent); backdrop-filter: blur(8px); border-bottom: 1px solid var(--border); }
.admin-bar-title { color: var(--text-muted); font-weight: 600; }
.admin-bar-title::before { content: '/'; margin-right: 12px; color: var(--text-faint); font-weight: 400; }
.admin-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.admin-actions select { width: auto; min-width: 136px; min-height: var(--ctrl-h-sm); height: var(--ctrl-h-sm); padding: 0 8px; font-size: var(--text-sm); }
.admin-hint { display: inline-flex; align-items: center; gap: 3px; color: var(--text-faint); font-size: var(--text-sm); white-space: nowrap; }
.admin-hint kbd { min-width: 18px; padding: 0 4px; border: 1px solid var(--border-strong); border-radius: 4px; font: inherit; font-size: var(--text-xs); line-height: 16px; text-align: center; }
.admin-hint kbd:last-of-type { margin-right: 3px; }
.admin-updated { color: var(--text-faint); font-size: var(--text-sm); font-variant-numeric: tabular-nums; white-space: nowrap; }
.admin-lead { margin: 18px 0 6px; font-size: var(--text-md); }

.admin-body { transition: opacity 160ms; }
.admin-body.is-refreshing { opacity: 0.6; }
.admin-section { margin: 26px 0 10px; color: var(--text-faint); font-size: var(--text-xs); font-weight: 650; letter-spacing: 0.06em; text-transform: uppercase; }
.admin-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.admin-cards-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-card, .admin-panel { background: var(--bg-elev); border: 1px solid var(--border); border-radius: var(--radius-md); }
.admin-card { display: flex; flex-direction: column; gap: 4px; padding: 16px 16px 12px; min-width: 0; }
.admin-card-label { color: var(--text-muted); font-size: var(--text-md); }
.admin-card strong { font-size: 26px; line-height: 1.15; font-variant-numeric: tabular-nums; }
.admin-card-sub { color: var(--text-faint); font-size: var(--text-sm); }
.admin-card .spark { margin-top: auto; padding-top: 8px; }

.admin-panel { margin-top: 14px; padding: 18px 20px; }
.admin-panel-head { display: flex; justify-content: space-between; align-items: start; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; }
.admin-panel-head p { margin: 4px 0 0; font-size: var(--text-md); }
.admin-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.admin-tab { display: inline-flex; align-items: center; gap: 6px; height: var(--ctrl-h-sm); padding: 0 12px; border: 1px solid var(--border); border-radius: var(--radius-pill); color: var(--text-muted); font-size: var(--text-sm); font-weight: 600; cursor: pointer; }
.admin-tab:hover { border-color: var(--border-strong); color: var(--text); }
.admin-tab.is-active { background: var(--bg-active); border-color: var(--border-strong); color: var(--text); }
.admin-tab-count { color: var(--text-faint); font-variant-numeric: tabular-nums; font-weight: 500; }

.admin-table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
th, td { padding: 9px 12px; text-align: right; border-top: 1px solid var(--border); white-space: nowrap; }
th { color: var(--text-faint); font-size: var(--text-xs); font-weight: 600; }
th:first-child, td:first-child { text-align: left; }
/* Ranked tables: the first column takes the slack and ellipsizes long URLs
   (full value on hover via title). */
.admin-ranked td:first-child { max-width: 0; width: 100%; overflow: hidden; text-overflow: ellipsis; }
.admin-ranked code { font-family: var(--mono-font); font-size: 0.92em; }
.admin-pseudo { color: var(--text-muted); font-style: italic; }
.admin-panel-flush { padding: 6px 20px 14px; }
.admin-limits td:first-child { font-weight: 600; }
.admin-limits-trend { width: 160px; }
.admin-limits td.admin-limits-trend .spark { width: 140px; height: 24px; margin-left: auto; }
.admin-closed { color: var(--text); font-weight: 600; }
.admin-muted { color: var(--text-muted); }
.admin-warn { color: var(--warning); font-weight: 600; }
.admin-footnote { margin: 10px 0 0; font-size: var(--text-sm); color: var(--text-faint); }
.admin-storage { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.admin-storage span { display: block; color: var(--text-muted); font-size: var(--text-md); }
.admin-storage div + div { border-left: 1px solid var(--border); padding-left: 20px; }
.admin-storage strong { display: block; margin-top: 3px; font-size: 18px; }
.admin-empty { padding: 28px; text-align: center; color: var(--text-faint); }
@media (max-width: 900px) {
  .admin-cards-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
  .admin-dashboard { padding: 0 16px 48px; }
  .admin-login { padding: 24px; }
  .admin-bar { flex-wrap: wrap; }
  .admin-actions { width: 100%; margin-left: 0; }
  .admin-actions select { flex: 1; }
  .admin-updated, .admin-hint { display: none; }
  .admin-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .admin-storage { grid-template-columns: 1fr; }
  .admin-storage div + div { border-left: 0; border-top: 1px solid var(--border); padding: 14px 0 0; }
}
</style>
